import { useEffect, useState } from 'react';
import {
  AnalysisRequest,
  formatPrice,
  type AnalysisRequestInput,
  type RequestedObjective,
  type Squad,
  type SquadPlayer,
  type SquadValidation,
} from '@fantasy/types';
import { ChipStatus } from './ChipStatus.tsx';
import { Term } from './Term.tsx';
import { ApiRequestError, api, type LivePlayerPoints } from '../api.ts';
import { useSquad } from '../state/useSquad.ts';

/** Starting XI is grouped and displayed in this order, matching the shirt
 * numbers convention — the same order the screenshot-import preview uses. */
const POSITION_ORDER = ['GKP', 'DEF', 'MID', 'FWD'] as const;

/** Parsing `{}` against the schema is the actual server-side defaults
 * (horizon 3, max 2 transfers, no points hits) — reusing them here means
 * these controls can't quietly drift out of sync with what the backend
 * would default to anyway if the frontend sent nothing at all. */
const DEFAULT_ANALYSIS_REQUEST = AnalysisRequest.parse({});

/** Worded for a manager, not by the API's own enum values — each carries the
 * trade-off it's making so the choice means something without having read
 * the backend ticket that introduced it. Exactly as T-056 worded them —
 * `AUTO_OBJECTIVE_OPTION` below is the only addition T-079 makes. */
const OBJECTIVE_OPTIONS: { value: RequestedObjective; label: string; description: string }[] = [
  {
    value: 'climb',
    label: 'Chasing a rank',
    description: 'Favours differentials and takes on more variance to make up ground fast.',
  },
  {
    value: 'protect',
    label: 'Protecting a rank',
    description: 'Favours template picks that defend the rank you already have.',
  },
  {
    value: 'balanced',
    label: 'Straight points',
    description: "Weighs the numbers alone, with no eye on anyone else's squad.",
  },
];

/** T-077's fourth request value — only meaningful for a squad with an FPL
 * entry id to read a rank from, so it's added to the option list below
 * rather than living in `OBJECTIVE_OPTIONS` itself. */
const AUTO_OBJECTIVE_OPTION: { value: RequestedObjective; label: string; description: string } = {
  value: 'auto',
  label: 'Decide for me from my rank',
  description: 'Reads your actual standing and picks climb, protect or balanced from it — and says why.',
};

interface SquadViewProps {
  squad: Squad;
  /** Null until the first validation has run. Only consulted here to flag
   * the squad-value stat when the budget rule specifically is broken — the
   * violations themselves are still App.tsx's own panel, not repeated here. */
  validation: SquadValidation | null;
  /** Re-runs analysis on this squad as it stands — reachable at any time,
   * not just automatically after an import or load. Takes the manager's
   * chosen horizon/transfer-cap/hit-allowance rather than always using the
   * server's bare defaults. */
  onAnalyse: (options: AnalysisRequestInput) => void;
  /** Opens the edit panel pre-filled with this squad, to fix a mistake or
   * make a change without retyping anything. */
  onEdit: () => void;
  analysing: boolean;
  /** Bumped by App.tsx when a squad is loaded from the library (or returned
   * to) — fetches this gameweek's points immediately rather than waiting
   * for a manual "Live points" click, so switching to an older squad shows
   * what actually happened in it right away. Not bumped for a fresh import,
   * where there's usually nothing to show yet. `undefined`/`0` fetches
   * nothing, matching every other squad-load path in this app. */
  fetchLivePointsSignal?: number;
}

/**
 * The squad itself, laid out on a pitch — starting XI grouped by position,
 * then bench in bench order — shown as soon as a squad is loaded or created,
 * independent of whether analysis has finished (or even started). This is
 * the only place in the app that renders the squad as a squad, rather than
 * as analysis output ranked by score.
 */
export function SquadView({
  squad,
  validation,
  onAnalyse,
  onEdit,
  analysing,
  fetchLivePointsSignal,
}: SquadViewProps) {
  const [horizon, setHorizon] = useState(DEFAULT_ANALYSIS_REQUEST.horizon);
  const [maxTransfers, setMaxTransfers] = useState(DEFAULT_ANALYSIS_REQUEST.maxTransfers);
  const [allowHits, setAllowHits] = useState(DEFAULT_ANALYSIS_REQUEST.allowHits);

  // Lives in SquadProvider, not local state here — same reasoning as
  // planHorizon/planAllowHits: it should survive this component unmounting,
  // not reset every time the squad view remounts.
  const {
    objective,
    setObjective,
    leagueId,
    updateToGameweek,
    updatingToGameweek,
    updateToGameweekProblem,
    updateToGameweekNotice,
    checkForGameweekAdvance,
    gameweekAdvanceNotice,
  } = useSquad();

  // Checks once per squad shown (mount, and again on remount — navigating
  // away and back re-mounts this view) whether FPL has genuinely advanced
  // past this squad's own gameweek since it was loaded, so a manager who's
  // actually looking at their squad when that happens finds out here rather
  // than only on their next full reload (T-127). Nothing to check for a
  // squad with no FPL entry id.
  useEffect(() => {
    if (squad.entryId !== undefined) void checkForGameweekAdvance(squad.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deliberately
    // keyed on squad.id alone, matching fetchLivePointsSignal's effect below.
  }, [squad.id]);

  // The 'auto' option only means anything for a squad with a rank to read —
  // offering it for one with no FPL entry id would let a manager pick a
  // choice the server can only resolve to 'balanced' with an "unknown
  // standing" reason, which reads as broken rather than as a real option.
  const hasEntryId = squad.entryId !== undefined;
  const objectiveOptions = hasEntryId ? [AUTO_OBJECTIVE_OPTION, ...OBJECTIVE_OPTIONS] : OBJECTIVE_OPTIONS;

  // Read-only and independent of analysis/edit — a manager can fetch this at
  // any time, and re-fetch it as often as they like, without it blocking or
  // interacting with either of those.
  const [livePoints, setLivePoints] = useState<Map<number, LivePlayerPoints> | null>(null);
  const [loadingLivePoints, setLoadingLivePoints] = useState(false);
  const [livePointsError, setLivePointsError] = useState<string | null>(null);

  async function onFetchLivePoints() {
    setLoadingLivePoints(true);
    setLivePointsError(null);
    try {
      const { points } = await api.getLivePoints(squad.id);
      setLivePoints(new Map(points.map((p) => [p.fplId, p])));
    } catch (error) {
      setLivePointsError(
        error instanceof ApiRequestError ? error.message : 'Something went wrong fetching live points.',
      );
    } finally {
      setLoadingLivePoints(false);
    }
  }

  // A different squad's points must never linger under a new one's players
  // (a shared fplId between two squads would otherwise show a stale, wrong
  // reading until the next manual fetch) — cleared on every squad switch,
  // whichever squad it came from.
  useEffect(() => {
    setLivePoints(null);
    setLivePointsError(null);
  }, [squad.id]);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally
  // keyed on the signal alone: it's bumped exactly when App.tsx wants a
  // fresh fetch (library load or "back to"), regardless of whether squad.id
  // actually changed (re-selecting the same squad should still refresh).
  useEffect(() => {
    if (fetchLivePointsSignal) void onFetchLivePoints();
  }, [fetchLivePointsSignal]);

  const bench = squad.players
    .filter((player) => !player.isStarting)
    .sort((a, b) => (a.benchOrder ?? 0) - (b.benchOrder ?? 0));
  const spent = squad.players.reduce((total, player) => total + player.price, 0);
  const overBudget = validation?.violations.some((v) => v.code === 'BUDGET') ?? false;

  return (
    <section className="panel panel--pitch rise-in" aria-labelledby="squad-view-heading">
      <div className="panel-head">
        <div>
          {/* h2, not h3 (T-100): the squad desk's hero <h2> only renders
           * before a squad exists (see SquadRoute) — once one is loaded,
           * this is the first heading under the masthead's h1, and an h3
           * here skipped a level. */}
          <h2 id="squad-view-heading">{squad.name}</h2>
          <p className="hint">Gameweek {squad.gameweek}</p>
          {/* One-time, not a permanent banner (T-127) — cleared by
           * SquadProvider the next time any squad is adopted, so it never
           * reappears for a squad already sitting on this, now-current,
           * gameweek. */}
          {gameweekAdvanceNotice && <p className="hint">{gameweekAdvanceNotice}</p>}
        </div>
        <div className="row row--flush">
          <button type="button" className="ghost small" onClick={onEdit}>
            Edit my squad
          </button>
          <button
            type="button"
            className="ghost small"
            onClick={onFetchLivePoints}
            disabled={loadingLivePoints}
            aria-busy={loadingLivePoints}
          >
            {loadingLivePoints && <span className="spinner" aria-hidden="true" />}
            {loadingLivePoints ? 'Loading…' : 'Live points'}
          </button>
          <button
            type="button"
            className="small"
            onClick={() => onAnalyse({ horizon, maxTransfers, allowHits, objective, leagueId: leagueId ?? undefined })}
            disabled={analysing}
            aria-busy={analysing}
          >
            {analysing && <span className="spinner" aria-hidden="true" />}
            {analysing ? 'Analysing…' : 'Analyse my squad'}
          </button>
        </div>
      </div>

      <ChipStatus squad={squad} />

      {/* The explicit, destructive counterpart to the passive bank/chip
       * refresh that already happens on every load — that refresh never
       * touches players or gameweek; this does, on request, in place (T-126).
       * Only offered for a squad with an FPL entry id — there is nothing to
       * update it against otherwise. Not offered while the edit panel is
       * open: SquadRoute only renders this view when `!editOpen`, so a
       * manager mid-edit never sees this button at all. */}
      {hasEntryId && (
        <div className="row mt-md">
          <button
            type="button"
            className="ghost small"
            onClick={() => updateToGameweek(squad.id)}
            disabled={updatingToGameweek}
            aria-busy={updatingToGameweek}
          >
            {updatingToGameweek && <span className="spinner" aria-hidden="true" />}
            {updatingToGameweek ? 'Updating…' : 'Update to this gameweek'}
          </button>
          <p className="hint">
            Replaces this squad&rsquo;s players, gameweek, bank, free transfers and chips with what FPL
            currently has for your linked team &mdash; not the passive refresh that already keeps your
            bank and chips current, which never touches your players. Your squad&rsquo;s own name is
            kept; everything else is overwritten and can&rsquo;t be undone.
          </p>
        </div>
      )}

      {updateToGameweekProblem && (
        <ul className="notices mt-md">
          <li className="error">{updateToGameweekProblem}</li>
        </ul>
      )}

      {/* Not an error (T-127) — FPL genuinely has nothing newer to serve yet,
       * and nothing was touched, so this reads as a plain explanation rather
       * than the alarming style `updateToGameweekProblem` uses. */}
      {updateToGameweekNotice && <p className="hint mt-md">{updateToGameweekNotice}</p>}

      {livePointsError && (
        <ul className="notices mt-md">
          <li className="error">{livePointsError}</li>
        </ul>
      )}

      {/* Collapsed by default: the server-matching defaults above are right
       * most of the time, so this stays out of the way until someone
       * actually wants a longer horizon, a different transfer cap, or to
       * allow a points hit. */}
      <details className="analysis-options">
        <summary className="analysis-options-summary">Analysis options</summary>
        <div className="row mt-md">
          <div className="field">
            <label htmlFor="analysis-horizon">Look ahead (gameweeks)</label>
            <input
              id="analysis-horizon"
              type="number"
              min={1}
              max={8}
              value={horizon}
              onChange={(e) => setHorizon(Number(e.target.value))}
            />
          </div>
          <div className="field">
            <label htmlFor="analysis-max-transfers">Max transfers to consider</label>
            <input
              id="analysis-max-transfers"
              type="number"
              min={0}
              max={15}
              value={maxTransfers}
              onChange={(e) => setMaxTransfers(Number(e.target.value))}
            />
          </div>
          <label className="checkbox-field">
            <input type="checkbox" checked={allowHits} onChange={(e) => setAllowHits(e.target.checked)} />
            Allow a -4 points hit
          </label>
        </div>

        <fieldset className="row mt-md">
          <legend>What's the goal?</legend>
          {objectiveOptions.map((option) => (
            <label key={option.value} className="checkbox-field">
              <input
                type="radio"
                name="objective"
                value={option.value}
                checked={objective === option.value}
                onChange={() => setObjective(option.value)}
              />
              <span>
                {option.label}
                <span className="hint"> — {option.description}</span>
              </span>
            </label>
          ))}
          {!hasEntryId && (
            <p className="hint">
              Import your squad with an FPL team id to let advice decide this for you, from your actual
              rank.
            </p>
          )}
        </fieldset>
      </details>

      <div className="stats mt-lg">
        <div className={overBudget ? 'callout callout--bad' : 'callout'}>
          <dt>
            <Term term="teamValue">Squad value</Term>
          </dt>
          <dd>
            <strong>{formatPrice(spent)}</strong>
          </dd>
        </div>
        <div className={overBudget ? 'callout callout--bad' : 'callout'}>
          <dt>In the bank</dt>
          <dd>
            <strong>{formatPrice(squad.bank)}</strong>
          </dd>
        </div>
      </div>

      <div className="pitch mt-xl">
        {POSITION_ORDER.map((position) => {
          const players = squad.players.filter(
            (player) => player.isStarting && player.position === position,
          );
          if (players.length === 0) return null;

          return (
            <div key={position} className="pitch-row">
              {players.map((player) => (
                <PlayerCard
                  key={player.name}
                  player={player}
                  live={player.fplId !== undefined ? livePoints?.get(player.fplId) : undefined}
                />
              ))}
            </div>
          );
        })}
      </div>

      <div className="dugout">
        <p className="dugout-label">Bench</p>
        <div className="dugout-row">
          {bench.map((player) => (
            <PlayerCard
              key={player.name}
              player={player}
              live={player.fplId !== undefined ? livePoints?.get(player.fplId) : undefined}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function PlayerCard({ player, live }: { player: SquadPlayer; live: LivePlayerPoints | undefined }) {
  return (
    <div className="pitch-card" data-club={player.club}>
      <div className="name">
        {player.name}
        {player.isCaptain && <span className="pill pill--captain">C</span>}
        {player.isViceCaptain && <span className="pill pill--vice">V</span>}
        {live && <span className="pill pill--good">{live.points} pts</span>}
      </div>
      <div className="meta">
        {player.club} · {formatPrice(player.price)}
        {live && ` · ${live.minutes}'`}
      </div>
    </div>
  );
}
